import { useMemo, type ReactElement } from 'react';
import { Button, Card, Col, Empty, Row, Space, Spin, Tag, Typography } from 'antd';
import { ArrowLeft, ThumbsDown, ThumbsUp, TrendingDown, TrendingUp, Vote } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { sssApi } from '@api/sss';
import { ROUTES } from '@shared/constants/routes';
import type { SssAnalyticsRow } from '../types';

const { Text, Title } = Typography;

const TOP_N = 5;
const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

interface RankedListProps {
  title: string;
  icon: ReactElement;
  rows: SssAnalyticsRow[];
  metric: 'ratio' | 'votes';
  empty?: string;
}

function RankedList({ title, icon, rows, metric, empty }: RankedListProps) {
  const { t } = useTranslation(['sss', 'common']);
  const emptyText = empty ?? t('analytics.noData');
  return (
    <Card
      size="small"
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {icon}
          {title}
        </span>
      }
    >
      {rows.length === 0 ? (
        <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={0}>
          {rows.map((row, idx) => (
            <div
              key={row.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '8px 0',
                borderBottom:
                  idx === rows.length - 1 ? undefined : '1px solid var(--ds-border-subtle)',
              }}
            >
              <Link
                to={ROUTES.SSS_EDIT.replace(':id', row.id)}
                style={{ minWidth: 0, flex: 1, wordBreak: 'break-word', fontSize: 13 }}
              >
                {row.question}
              </Link>
              <Tag
                color={metric === 'ratio' ? (row.yesRatio >= 0.5 ? 'green' : 'red') : 'blue'}
                style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}
              >
                {metric === 'ratio'
                  ? t('analytics.ratioTag', { pct: pct(row.yesRatio), votes: row.totalVotes })
                  : t('analytics.votesTag', { votes: row.totalVotes.toLocaleString('tr-TR') })}
              </Tag>
            </div>
          ))}
        </Space>
      )}
    </Card>
  );
}

export function SssAnalyticsPage() {
  const { t } = useTranslation(['sss', 'common']);
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['sss-analytics', TOP_N],
    queryFn: () => sssApi.analytics(TOP_N),
  });

  const mostVotedChart = useMemo(
    () =>
      (data?.mostVoted ?? []).map((r) => ({
        name: truncate(r.question, 30),
        yes: r.helpfulYesCount,
        no: r.helpfulNoCount,
      })),
    [data?.mostVoted],
  );

  const header = (
    <PageHeader
      title={t('analytics.title')}
      description={t('analytics.description')}
      breadcrumb={[
        { label: t('common:labels.home'), to: '/' },
        { label: 'SSS', to: ROUTES.SSS },
        { label: t('list.analytics') },
      ]}
      actions={
        <Button icon={<ArrowLeft size={14} />} onClick={() => void navigate(ROUTES.SSS)}>
          {t('analytics.backToList')}
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div>
        {header}
        <Card>
          <Empty description={t('analytics.noAnalytics')} />
        </Card>
      </div>
    );
  }

  const totalVotes = data.totalYes + data.totalNo;

  return (
    <div>
      {header}

      <Row gutter={[16, 16]} className="sss-analytics__cards">
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('analytics.totalItems')}
            </Text>
            <Title level={2} style={{ margin: '6px 0 4px', fontSize: 28, fontWeight: 700 }}>
              {data.totalItems.toLocaleString('tr-TR')}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('analytics.totalPublished', { value: data.totalPublished.toLocaleString('tr-TR') })}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('analytics.totalHelpfulVotes')}
            </Text>
            <Title level={2} style={{ margin: '6px 0 4px', fontSize: 28, fontWeight: 700 }}>
              {totalVotes.toLocaleString('tr-TR')}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <Vote size={11} style={{ verticalAlign: -2 }} /> {t('analytics.totalVoting')}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('analytics.yes')}
            </Text>
            <Title
              level={2}
              style={{
                margin: '6px 0 4px',
                fontSize: 28,
                fontWeight: 700,
                color: '#10b981',
              }}
            >
              {data.totalYes.toLocaleString('tr-TR')}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ThumbsUp size={11} style={{ verticalAlign: -2 }} /> {t('analytics.helpfulYes')}
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t('analytics.overallRatio')}
            </Text>
            <Title
              level={2}
              style={{
                margin: '6px 0 4px',
                fontSize: 28,
                fontWeight: 700,
                color: data.overallRatio >= 0.5 ? '#10b981' : '#ef4444',
              }}
            >
              {pct(data.overallRatio)}
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <ThumbsDown size={11} style={{ verticalAlign: -2 }} />{' '}
              {t('analytics.totalNo', { value: data.totalNo.toLocaleString('tr-TR') })}
            </Text>
          </Card>
        </Col>
      </Row>

      <Card title={t('analytics.mostVoted', { value: TOP_N })} style={{ marginTop: 16 }}>
        {mostVotedChart.length === 0 ? (
          <Empty description={t('analytics.noVotesYet')} />
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, mostVotedChart.length * 56)}>
            <BarChart
              data={mostVotedChart}
              layout="vertical"
              margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
            >
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value: number, key: string) => [
                  value.toLocaleString('tr-TR'),
                  key === 'yes' ? t('analytics.chartYes') : t('analytics.chartNo'),
                ]}
              />
              <Bar dataKey="yes" stackId="v" fill="#10b981" radius={[0, 0, 0, 0]} />
              <Bar dataKey="no" stackId="v" fill="#ef4444" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <RankedList
            title={t('analytics.mostHelpful')}
            icon={<TrendingUp size={14} color="#10b981" />}
            rows={data.mostHelpful}
            metric="ratio"
          />
        </Col>
        <Col xs={24} md={12}>
          <RankedList
            title={t('analytics.leastHelpful')}
            icon={<TrendingDown size={14} color="#ef4444" />}
            rows={data.leastHelpful}
            metric="ratio"
          />
        </Col>
      </Row>
    </div>
  );
}

export default SssAnalyticsPage;
